/**
 * QuestCells — крупные ячейки cfg-grid формы квеста: «Спецверсии» (A35, чипы
 * Δ + Dropdown привязки; prefill первой привязки делает хук), «Связи»
 * (linked-groups: чипы short-имён + Dropdown multi «Связать локации»),
 * ценовой ряд cell-price из 3 NumPad-ячеек (эталон specsCell/linksCell/
 * priceGrid, js:270-280, 399-415).
 */
import { useState } from "react";
import { Dropdown } from "../../components/shared";
import type { Quest, QuestVersion } from "../../api/types";
import { money } from "./EoConfigTeas";
import { fmtD, NumCell } from "./QuestFormCells";
import { CrossIco } from "./questIcons";
import cfg from "./config.module.css";
import css from "./quests.module.css";

export function SpecsCell({ q, versions, onBind, onUnbind }: {
  q: Quest;
  versions: QuestVersion[];
  onBind: (v: QuestVersion) => void;
  onUnbind: (v: QuestVersion) => void;
}) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const mine = versions.filter((v) => v.slot_quest_id === q.id);
  return (
    <div className={cfg.cell} style={{ minWidth: 0 }}>
      <span className={cfg.klbl}>Спецверсии</span>
      <div className={`${css.chips} ${css.oneline}`}>
        {mine.map((v) => (
          <span key={v.id} className={css.chip}>
            <span className={css.ct}>{v.title}</span>
            <span className={`${css.sdelta} ${css.num}`}>{fmtD((v.duration_min ?? q.duration) - q.duration)}</span>
            <button type="button" className={css.cx} onClick={() => onUnbind(v)}><CrossIco /></button>
          </span>
        ))}
        {versions.length ? (
          <button
            type="button"
            className={css.selBtn}
            title="Привязать спецверсию"
            onClick={(e) => setDd(e.currentTarget)}
          >
            {mine.length ? "+" : "+ спецверсия"}
          </button>
        ) : null}
      </div>
      <Dropdown
        open={!!dd}
        anchor={dd}
        title="Спецверсии"
        items={versions.map((v) => ({
          id: v.id,
          label: v.title,
          meta: v.slot_quest_id
            ? v.slot_quest_id === q.id ? "уже здесь" : v.base_short ?? undefined
            : undefined,
          disabled: !!v.slot_quest_id,
        }))}
        onCommit={(ids) => {
          for (const id of ids) {
            const v = versions.find((x) => x.id === id);
            if (v && !v.slot_quest_id) onBind(v);
          }
        }}
        onClose={() => setDd(null)}
      />
    </div>
  );
}

export function LinksCell({ q, all, links, onChange }: {
  q: Quest;
  all: Quest[];
  links: string[];
  onChange: (next: string[]) => void;
}) {
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const others = all.filter((x) => x.id !== q.id);
  const shortOf = (id: string) => all.find((x) => x.id === id)?.short_name ?? id;
  return (
    <div className={cfg.cell} style={{ minWidth: 0 }}>
      <span className={cfg.klbl}>Связи</span>
      <div className={`${css.chips} ${css.oneline}`}>
        {links.map((id) => (
          <span key={id} className={css.chip}>
            <span className={css.ct}>{shortOf(id)}</span>
            <button type="button" className={css.cx} onClick={() => onChange(links.filter((x) => x !== id))}>
              <CrossIco />
            </button>
          </span>
        ))}
        {others.length ? (
          <button
            type="button"
            className={`${css.selBtn} ${css.selPlus}`}
            title="Добавить связь"
            onClick={(e) => setDd(e.currentTarget)}
          >
            +
          </button>
        ) : null}
      </div>
      <Dropdown
        open={!!dd}
        anchor={dd}
        title="Связать локации"
        items={others.map((x) => ({ id: x.id, label: x.short_name, disabled: links.includes(x.id) }))}
        onCommit={(ids) => {
          const fresh = ids.map(String).filter((id) => !links.includes(id));
          if (fresh.length) onChange([...links, ...fresh]);
        }}
        onClose={() => setDd(null)}
      />
    </div>
  );
}

/** Ценовой ряд «До, чел / База / +₽/чел» — full-width cell-price (js:399-403);
 *  three — отдельная сетка cfg-grid three (спецверсии, js:399, 463): это
 *  последний cfg-grid тела карточки → role-тинт границ сверху и снизу
 *  (правила .qbody html:93-94, статично как gridTop/gridBot). */
export function PriceRow({ pn, pb, pp, onPn, onPb, onPp, three }: {
  pn: number; pb: number; pp: number;
  onPn: (v: number) => void; onPb: (v: number) => void; onPp: (v: number) => void;
  three?: boolean;
}) {
  return (
    <div className={three ? `${cfg.cfgGrid} ${cfg.three} ${css.gridTop} ${css.gridBot}` : css.cellPrice}>
      <NumCell
        center
        label="До, чел"
        value={<span className={css.num}>{pn}</span>}
        np={{ label: "Базовая вместимость", mode: "int", unit: "чел", max: 30, value: pn }}
        onCommit={(v) => onPn(v || pn)}
      />
      <NumCell
        center
        label="База"
        value={<span className={css.num}>{money(pb)}</span>}
        np={{ label: `Цена до ${pn} чел`, mode: "money", value: pb, presets: [3600, 4800, 7200] }}
        onCommit={onPb}
      />
      <NumCell
        center
        label="+₽/чел"
        value={<span className={css.num}>{money(pp)}</span>}
        np={{ label: "Доплата за человека", mode: "money", value: pp, presets: [500, 600, 800] }}
        onCommit={onPp}
      />
    </div>
  );
}
